import { Controller } from "@hotwired/stimulus"

// Manages the home page vehicle selector:
// - Category nav: pills jump to their section; the bar sticks under the
//   navbar on scroll and the in-view section's pill stays highlighted
// - Per-card year / version / motor dropdowns (custom-select components)
// - CTA becomes clickable only once all three are picked
export default class extends Controller {
  static targets = ["categoryPill", "categorySection", "nav"]

  connect() {
    this.element.querySelectorAll("[data-car-slug]").forEach(card => {
      this.applyYearFilter(card)
      this.applyMotorFilter(card)
      this.updateSelectLocks(card)
      this.updateCtaState(card)
    })

    // Pre-fill each card with sensible defaults so it's ready to go without
    // opening all three dropdowns. Deferred one frame because the per-select
    // custom-select controllers are descendants and connect after this one.
    requestAnimationFrame(() => {
      this.element.querySelectorAll("[data-car-slug]").forEach(card => this.preselectDefaults(card))
    })

    // Scrollspy: park the nav under the live navbar and keep the pill for
    // the section currently in view highlighted as the user scrolls.
    this._suppressSpyUntil = 0
    this.onScroll = this.onScroll.bind(this)
    this.onResize = this.onResize.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onResize)
    this.measureNavTop()
    this.syncActivePill()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onResize)
    if (this._raf) cancelAnimationFrame(this._raf)
    clearTimeout(this._spyResyncTimer)
  }

  // Height of the sticky public navbar — the bar parks flush under it.
  navbarHeight() {
    const navbar = document.querySelector("nav.sticky")
    return navbar ? navbar.getBoundingClientRect().height : 60
  }

  // Y offset occupied by the navbar + the stuck category bar. A section
  // scrolled to this line sits just below the sticky chrome.
  stickyOffset() {
    const barHeight = this.hasNavTarget ? this.navTarget.getBoundingClientRect().height : 0
    return this.navbarHeight() + barHeight
  }

  measureNavTop() {
    if (this.hasNavTarget) {
      this.navTarget.style.setProperty("--ford-category-nav-top", `${this.navbarHeight()}px`)
    }
  }

  onResize() {
    this.measureNavTop()
    this.syncActivePill()
  }

  onScroll() {
    if (this._raf) return
    this._raf = requestAnimationFrame(() => {
      this._raf = null
      this.syncActivePill()
    })
  }

  scrollToCategory(event) {
    event.preventDefault()
    const slug = event.currentTarget.dataset.category
    const section = this.categorySectionTargets.find(s => s.dataset.category === slug)
    if (!section) return

    this.setActivePill(slug)
    event.currentTarget.blur()

    // Suppress the scrollspy during the programmatic smooth-scroll so the
    // clicked pill doesn't flicker through the categories it passes. Then
    // force one re-sync when the window ends: on mobile a flick fires a
    // short burst of scroll events that can land entirely inside this window
    // (and momentum then stops), leaving no later event to update the pill —
    // which is why the highlight got stuck there but not on desktop.
    this._suppressSpyUntil = Date.now() + 600
    clearTimeout(this._spyResyncTimer)
    this._spyResyncTimer = setTimeout(() => this.syncActivePill(), 650)

    const top = section.getBoundingClientRect().top + window.scrollY - this.stickyOffset() - 8
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" })
  }

  // Highlight the pill for the last section whose top has passed under the
  // sticky chrome — i.e. the one the user is currently looking at.
  syncActivePill() {
    if (!this.hasCategorySectionTarget) return
    if (Date.now() < this._suppressSpyUntil) return

    const line = this.stickyOffset() + 12
    let current = this.categorySectionTargets[0]
    for (const section of this.categorySectionTargets) {
      if (section.getBoundingClientRect().top <= line) current = section
      else break
    }
    if (current) this.setActivePill(current.dataset.category)
  }

  setActivePill(slug) {
    this.categoryPillTargets.forEach(pill => {
      pill.classList.toggle("active", pill.dataset.category === slug)
    })
  }

  // Default the card to the most recent year, then the first available
  // version and motor. Selecting the year cascades (unlock + filter) before
  // we pick the version, and likewise for the motor — so each step lands on
  // the first option that's actually valid for the prior choices. A
  // sub-select with a single option simply gets that option auto-filled.
  preselectDefaults(card) {
    const defaults = {
      year: card.dataset.defaultYear,
      version: card.dataset.defaultVersion,
      motor: card.dataset.defaultMotor
    }

    for (const type of [ "year", "version", "motor" ]) {
      if (defaults[type] && this.selectOption(card, type, defaults[type])) continue

      this.selectFirstOption(card, type)
    }
  }

  selectFirstOption(card, type) {
    const hidden = card.querySelector(`[data-select-type='${type}']`)
    if (!hidden || hidden.value) return

    const container = hidden.closest(".ford-card-select")
    const option = [ ...container.querySelectorAll(".ford-card-select__option") ]
      .find(opt => opt.style.display !== "none")
    if (option) this.selectOption(card, type, option.dataset.value)
  }

  selectOption(card, type, value) {
    const hidden = card.querySelector(`[data-select-type='${type}']`)
    if (!hidden) return false
    if (hidden.value) return hidden.value === value

    const customSelectEl = hidden.closest("[data-controller~='custom-select']")
    const ctrl = customSelectEl &&
      this.application.getControllerForElementAndIdentifier(customSelectEl, "custom-select")
    if (!ctrl) return false

    const container = hidden.closest(".ford-card-select")
    const option = [ ...container.querySelectorAll(".ford-card-select__option") ]
      .find(opt => opt.dataset.value === value && opt.style.display !== "none")
    if (!option) return false

    ctrl.selectByValue(value)
    return hidden.value === value
  }

  // Fires from the hidden input inside each custom-select (change event
  // bubbles up from custom_select_controller#select).
  selectChanged(event) {
    const card = event.target.closest("[data-car-slug]")
    if (!card) return

    const type = event.target.dataset.selectType
    // Year picked → narrow Versão and Motorização to that year.
    // Versão picked → narrow Motorização to that version (still respecting
    // year if year is also picked).
    if (type === "year") {
      this.resetSelect(card, "version")
      this.resetSelect(card, "motor")
      this.applyYearFilter(card)
      this.applyMotorFilter(card)
    } else if (type === "version") {
      this.resetSelect(card, "motor")
      this.applyMotorFilter(card)
    }

    this.updateSelectLocks(card)
    this.updateCtaState(card)

    // After narrowing, auto-fill a downstream select that's left with a
    // single valid option — there's nothing to choose. (Selecting it
    // cascades, so a year change that leaves one version also fills the
    // motor when that version has a single one.)
    if (type === "year") {
      this.autofillIfSingle(card, "version")
    } else if (type === "version") {
      this.autofillIfSingle(card, "motor")
    }
  }

  // Picks the option for `type` only when exactly one is valid (visible)
  // and nothing is chosen yet. Cascades via the change event.
  autofillIfSingle(card, type) {
    const hidden = card.querySelector(`[data-select-type='${type}']`)
    if (!hidden || hidden.value) return

    const container = hidden.closest(".ford-card-select")
    const visible = [ ...container.querySelectorAll(".ford-card-select__option") ]
      .filter(opt => opt.style.display !== "none")
    if (visible.length !== 1) return

    const customSelectEl = hidden.closest("[data-controller~='custom-select']")
    const ctrl = customSelectEl &&
      this.application.getControllerForElementAndIdentifier(customSelectEl, "custom-select")
    if (ctrl) ctrl.selectByValue(visible[0].dataset.value)
  }

  // Hide Versão options whose available years don't include the picked
  // year. If the currently-selected Versão becomes hidden, reset it.
  applyYearFilter(card) {
    const year = card.querySelector("[data-select-type='year']")?.value || ""
    const versionInput = card.querySelector("[data-select-type='version']")
    if (!versionInput) return

    const container = versionInput.closest(".ford-card-select")
    const options = container.querySelectorAll(".ford-card-select__option")

    options.forEach(opt => {
      if (!year) {
        opt.style.display = ""
        return
      }
      const years = (opt.dataset.years || "").split(",").filter(Boolean)
      opt.style.display = years.includes(year) ? "" : "none"
    })

    this.resetIfHidden(versionInput, container)
  }

  // Hide Motorização options that don't match the currently-picked
  // year and/or version. If the selected motor becomes hidden, reset it.
  applyMotorFilter(card) {
    const year    = card.querySelector("[data-select-type='year']")?.value || ""
    const version = card.querySelector("[data-select-type='version']")?.value || ""
    const motorInput = card.querySelector("[data-select-type='motor']")
    if (!motorInput) return

    const container = motorInput.closest(".ford-card-select")
    const options = container.querySelectorAll(".ford-card-select__option")

    options.forEach(opt => {
      const optYear    = opt.dataset.year || ""
      const optVersion = opt.dataset.version || ""
      const yearOk    = !year    || optYear === year
      const versionOk = !version || optVersion === version
      opt.style.display = (yearOk && versionOk) ? "" : "none"
    })

    this.resetIfHidden(motorInput, container)
  }

  // If the currently-selected option in a custom-select got hidden by a
  // filter, clear it via the controller's #reset and re-fire change so
  // the CTA and any further downstream filters re-evaluate.
  resetIfHidden(hiddenInput, container) {
    const current = hiddenInput.value
    if (!current) return

    const selectedOpt = container.querySelector(
      `.ford-card-select__option[data-value="${CSS.escape(current)}"]`
    )
    if (!selectedOpt) return
    if (selectedOpt.style.display !== "none") return

    const customSelectEl = container.querySelector("[data-controller~='custom-select']")
    if (customSelectEl) {
      const ctrl = this.application.getControllerForElementAndIdentifier(customSelectEl, "custom-select")
      ctrl?.reset?.()
    }
    // custom_select_controller#reset doesn't dispatch change; do it here
    // so cascading filters (e.g. Versão reset → reapply Motor filter) run.
    hiddenInput.dispatchEvent(new Event("change", { bubbles: true }))
  }

  resetSelect(card, type) {
    const hiddenInput = card.querySelector(`[data-select-type='${type}']`)
    if (!hiddenInput?.value) return

    const customSelectEl = hiddenInput.closest("[data-controller~='custom-select']")
    if (customSelectEl) {
      const ctrl = this.application.getControllerForElementAndIdentifier(customSelectEl, "custom-select")
      ctrl?.reset?.()
    } else {
      hiddenInput.value = ""
    }
  }

  updateSelectLocks(card) {
    const year    = card.querySelector("[data-select-type='year']")?.value
    const version = card.querySelector("[data-select-type='version']")?.value

    this.setSelectLocked(card, "version", !year)
    this.setSelectLocked(card, "motor", !(year && version))
  }

  setSelectLocked(card, type, locked) {
    const hiddenInput = card.querySelector(`[data-select-type='${type}']`)
    if (!hiddenInput) return

    const customSelectEl = hiddenInput.closest("[data-controller~='custom-select']")
    const button = customSelectEl?.querySelector("[data-custom-select-target='button']")
    if (!button) return

    customSelectEl.classList.toggle("is-disabled", locked)
    button.disabled = locked
    button.setAttribute("aria-disabled", String(locked))

    if (!locked) return

    const ctrl = this.application.getControllerForElementAndIdentifier(customSelectEl, "custom-select")
    if (!ctrl) return

    ctrl.openValue = false
    ctrl.render?.()
  }

  updateCtaState(card) {
    const cta = card.querySelector("[data-home-selector-target='ctaButton']")
    if (!cta) return

    const year    = card.querySelector("[data-select-type='year']")?.value
    const version = card.querySelector("[data-select-type='version']")?.value
    const motor   = card.querySelector("[data-select-type='motor']")?.value

    const ready = year && version && motor

    cta.classList.toggle("ford-card-cta--disabled", !ready)
    cta.setAttribute("aria-disabled", String(!ready))
  }

  goToExperience(event) {
    event.preventDefault()
    const card = event.currentTarget.closest("[data-car-slug]")
    if (!card) return

    const carSlug = card.dataset.carSlug
    if (!carSlug) return

    const year    = card.querySelector("[data-select-type='year']")?.value
    const version = card.querySelector("[data-select-type='version']")?.value
    const motor   = card.querySelector("[data-select-type='motor']")?.value

    // Require all three so the experience page knows exactly which
    // model the user wants.
    if (!year || !version || !motor) return

    const params = new URLSearchParams({ year, version, model: motor })
    const url = `/cars/${carSlug}?${params.toString()}`

    // Full reload — the public layout disables Turbo Drive (see the body
    // tag in layouts/public.html.erb) so the Botmaker chat widget isn't
    // torn down on every nav.
    window.location.href = url
  }
}
